import type { ResolvedCollection } from '@rstore/shared';
import type { VueStore } from '@rstore/vue';
import type { AnyItem, CompositePrimaryKey, PrimitivePrimaryKey, ResolvedMonospaceCollection, ResolvedMonospaceSingleConstraint } from '#layers/data-model/public';
import type { WorkspaceRStore } from '~/rstore/create-workspace-rstore';
import { getCollectionPrimaryKeyFieldNames, getRelationConstraintFields, hasCompositePrimaryKey, parseItemPrimaryKeyString, ReferentialAction, stringifyStableId, toCompositePrimaryKey } from '#layers/data-model/public';
import { resolveSchemaCollection } from './resolve-schema-collection';

/** A foreign key constraint together with the schema collection whose rows hold it. */
interface ForeignKey {
	collection: ResolvedMonospaceCollection;
	constraint: ResolvedMonospaceSingleConstraint;
}

/**
 * Mirror the database's referential actions in the cache once rows are deleted server-side.
 *
 * rstore only drops the deleted rows themselves. Rows in other collections whose foreign key
 * points at them stay cached, and to-many relations resolve through FK indexes, so a junction
 * whose target was deleted keeps showing up as a related item with nothing to display until a
 * full reload. The engine has already applied each constraint's `onDelete`: cascade-deleted rows
 * are dropped here as well, recursively, and set-null rows get their foreign key cleared. Other
 * actions leave dependents untouched; restrict and no-action would have failed the delete.
 *
 * Not handled: a foreign key referencing a non-primary unique index. The deleted key is built from
 * the primary key alone, and the referenced values cannot be read back because the optimistic
 * layer already hides the row by the time this runs.
 *
 * Never throws. This runs after the server delete succeeded, and an error out of the hook makes
 * rstore roll the optimistic layer back and resurrect a row the server no longer has. A row that
 * cannot be addressed in the cache is skipped instead.
 */
export function applyDeleteCascadeToCache(store: VueStore, collection: ResolvedCollection, keys: PrimitivePrimaryKey[]): void {
	const schemaCollection = resolveSchemaCollection({ collectionName: collection.name, store });

	// No primary key fields, nothing to build a deleted key from.
	if (!schemaCollection || getCollectionPrimaryKeyFieldNames(schemaCollection).length === 0) return;

	const deletedKeys = keys.map((key) => toDeletedKey(key, schemaCollection));
	const visited = new Set(deletedKeys.map((key) => toVisitKey(schemaCollection, key)));

	cascadeDelete(store, schemaCollection, deletedKeys, visited);
}

/** One cascade level: applies every foreign key pointing at the deleted rows, then recurses into what it deleted. */
function cascadeDelete(store: VueStore, schemaCollection: ResolvedMonospaceCollection, deletedKeys: CompositePrimaryKey[], visited: Set<string>): void {
	for (const { collection: dependentSchemaCollection, constraint } of findForeignKeysTo(store, schemaCollection)) {
		const action = constraint.onDelete;
		// TODO investigate ReferentialAction.SetDefault, might need more data in the server response about the affected rows
		if (action !== ReferentialAction.Cascade && action !== ReferentialAction.SetNull) continue;

		const dependentCollection = store.$collections.find((collection) => collection.name === dependentSchemaCollection.apiName);
		if (!dependentCollection) continue;

		// One cache read and one recursion per foreign key, not per deleted row, so a bulk delete
		// scans each dependent collection once.
		const dependents = store.$cache.readItems({
			collection: dependentCollection,
			filter: (item) => deletedKeys.some((deletedKey) => referencesKey(item, constraint, deletedKey)),
		});

		const dependentKeyFieldNames = getCollectionPrimaryKeyFieldNames(dependentSchemaCollection);
		const cascadedKeys: CompositePrimaryKey[] = [];

		for (const dependent of dependents) {
			const dependentKey = toItemKey(dependent, dependentKeyFieldNames);
			if (!dependentKey) continue;

			const cacheKey = dependent.$getKey();
			if (cacheKey == null) continue;

			if (action === ReferentialAction.SetNull) {
				store.$cache.writeItem({ collection: dependentCollection, key: cacheKey, item: nulledForeignKey(constraint) });
				continue;
			}

			const visitKey = toVisitKey(dependentSchemaCollection, dependentKey);
			if (visited.has(visitKey)) continue;
			visited.add(visitKey);

			store.$cache.deleteItem({ collection: dependentCollection, key: cacheKey });
			cascadedKeys.push(dependentKey);
		}

		if (cascadedKeys.length > 0) {
			cascadeDelete(store, dependentSchemaCollection, cascadedKeys, visited);
		}
	}
}

/**
 * Every foreign key pointing at `referencedCollection`, found by scanning the side that holds the
 * constraint. A one-sided relation, with no field on the referenced collection, is included too.
 */
function findForeignKeysTo(store: VueStore, referencedCollection: ResolvedMonospaceCollection): ForeignKey[] {
	return ((store as WorkspaceRStore).MonospaceCollection?.peekMany() ?? []).flatMap((schemaCollection) =>
		schemaCollection.relationFields.flatMap(({ constraint, oppositeCollectionId }) =>
			constraint && oppositeCollectionId === referencedCollection.id
				? [{ collection: schemaCollection, constraint }]
				: [],
		),
	);
}

/**
 * Cycle guard identity for a row: collection api name plus stringified key.
 *
 * `stringifyStableId` is plain `JSON.stringify`, so field order matters. Both key sources list
 * fields in {@link getCollectionPrimaryKeyFieldNames} order, so the same row always stringifies the same.
 */
function toVisitKey(schemaCollection: ResolvedMonospaceCollection, key: CompositePrimaryKey): string {
	return `${schemaCollection.apiName}:${stringifyStableId(key)}`;
}

/** The deleted row's key as field values, so it can be matched against dependents' foreign keys. */
function toDeletedKey(key: PrimitivePrimaryKey, schemaCollection: ResolvedMonospaceCollection): CompositePrimaryKey {
	return hasCompositePrimaryKey(schemaCollection)
		? parseItemPrimaryKeyString(String(key), schemaCollection)
		: toCompositePrimaryKey(key, schemaCollection);
}

/**
 * A cached row's primary key, read off the item itself rather than parsed back out of its cache key.
 *
 * `fieldNames` are the collection's primary key field names, resolved once by the caller. Returns
 * `undefined` when the body lacks a key field: `getKey` would still stringify the fields present,
 * giving a key that matches no cache entry and cannot be parsed back into field values.
 */
function toItemKey(item: AnyItem, fieldNames: string[]): CompositePrimaryKey | undefined {
	if (fieldNames.length === 0) return undefined;

	const key: CompositePrimaryKey = {};

	for (const fieldName of fieldNames) {
		const value = item[fieldName];
		if (value == null) return undefined;

		key[fieldName] = value;
	}

	return key;
}

/** Whether the item's foreign key, across every mapping of the constraint, equals the deleted key. */
function referencesKey(item: AnyItem, constraint: ResolvedMonospaceSingleConstraint, deletedKey: CompositePrimaryKey): boolean {
	// No mappings means the constraint's field rows are not loaded, not that every row matches:
	// `[].every()` is true.
	if (constraint.fieldMappings.length === 0) return false;

	return constraint.fieldMappings.every(({ constrainedField, referencedField }) => {
		if (!constrainedField || !referencedField) return false;

		const value = deletedKey[referencedField.apiName];
		return value !== undefined && item[constrainedField.apiName] === value;
	});
}

/** The partial item write that clears the constraint's foreign key fields. */
function nulledForeignKey(constraint: ResolvedMonospaceSingleConstraint): AnyItem {
	const fields: AnyItem = {};

	for (const { apiName } of getRelationConstraintFields(constraint).constrainedFields) {
		fields[apiName] = null;
	}

	return fields;
}
